import { useContext, useId, type ReactElement } from "react";
import {
  Map as MapIcon,
  Maximize2,
  Minus,
  Plus,
  Undo2,
  Redo2,
} from "lucide-react";
import { ViewportControlsReveal } from "./ViewportControlsReveal.js";
import { CanvasNavigationContext } from "../lib/canvas-navigation-context.js";
import { Button } from "../atoms/Button/Button.js";
import { Tooltip } from "../molecules/Tooltip/Tooltip.js";
import {
  WorkspaceControlGroup,
  type WorkspaceControlPlacement,
} from "./WorkspaceControlGroup.js";

/** Optional host-owned history; omission preserves the shared viewport cluster. */
export interface WorkspaceHistoryControls {
  readonly undoLabel?: string | undefined;
  readonly redoLabel?: string | undefined;
  readonly disabled?: boolean | undefined;
  /**
   * Why history is off while `disabled`. When set, both buttons stay
   * focusable (`aria-disabled`), the tooltip says the reason and each button
   * is described by it, so a keyboard or screen-reader user can learn why.
   */
  readonly disabledReason?: string | undefined;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
}

/**
 * The viewport cluster every canvas module shares, bottom-start unless the host
 * names another `placement`: Zoom out · zoom % readout · Fit · Zoom in ·
 * optional mini-map toggle. One component so the three module canvases cannot
 * drift on icons, font, or chrome — SOLVE's design is the reference this
 * encodes (its bottom cluster was the one the other two were asked to match).
 *
 * **It reports, it does not drive.** Zoom and fit go back to the host as
 * callbacks, so a module keeps its own camera channel (clamps, reduced-motion
 * handling, doc-derived fit bounds). `zoom` is the React Flow scale (1 = 100%)
 * passed in rather than read here, which keeps this component free of any
 * React Flow dependency.
 *
 * **The readout is information, not a second Fit button.** It announces zoom
 * changes politely — focus stays on the pressed button, whose name never
 * changes, so the live region is the only place a screen reader hears the
 * result.
 */
export function WorkspaceViewportControls({
  zoom,
  onZoomIn,
  onZoomOut,
  onFit,
  fitLabel = "Fit canvas to view",
  label,
  miniMap,
  history,
  placement = "bottom-start",
  "data-testid": testId,
  "data-geometry": geometry,
}: {
  /** Where the cluster sits. Default `bottom-start`, today's corner. */
  readonly placement?: WorkspaceControlPlacement | undefined;
  /** React Flow scale; rendered as `Math.round(zoom * 100)%`. */
  readonly history?: WorkspaceHistoryControls | undefined;
  readonly zoom: number;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onFit: () => void;
  /**
   * The Fit button's name and tooltip, when Fit frames something narrower than
   * the whole canvas — e.g. "Fit SIPOC to view" (PB-5441 BUG-14). Defaults to
   * "Fit canvas to view", so an omitted prop changes nothing.
   */
  readonly fitLabel?: string | undefined;
  /** Accessible name for the group, e.g. "SOLVE viewport controls". */
  readonly label: string;
  /**
   * Mini-map toggle. Omit where the host offers no mini-map (below its
   * breakpoint, or on a view without one) and the button does not render.
   */
  readonly miniMap?:
    | { readonly visible: boolean; readonly onToggle: () => void }
    | undefined;
  readonly "data-testid"?: string;
  readonly "data-geometry"?: string;
}): ReactElement {
  const navigation = useContext(CanvasNavigationContext);
  const reasonId = useId();
  const reason = history?.disabled ? history.disabledReason : undefined;
  const controls = (
    <>
      {history ? (
        <>
          {(
            [
              [
                "Undo",
                history.undoLabel,
                history.onUndo,
                Undo2,
                "Control+Z Meta+Z",
              ],
              [
                "Redo",
                history.redoLabel,
                history.onRedo,
                Redo2,
                "Control+Shift+Z Meta+Shift+Z Control+Y",
              ],
            ] as const
          ).map(([verb, action, onClick, Icon, shortcut]) => (
            <Tooltip
              key={verb}
              // The reason is already the button's own description.
              describeChild={reason === undefined}
              content={
                reason ??
                (action
                  ? `${verb}: ${action}`
                  : `Nothing to ${verb.toLowerCase()}`)
              }
            >
              <Button
                size="sm"
                variant="ghost"
                onClick={onClick}
                disabled={history.disabled || !action}
                focusableWhenDisabled={reason !== undefined}
                aria-describedby={reason !== undefined ? reasonId : undefined}
                aria-label={action ? `${verb}: ${action}` : verb}
                aria-keyshortcuts={shortcut}
                data-canvas-history-control=""
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </Tooltip>
          ))}
          {reason !== undefined ? (
            <span id={reasonId} className="sr-only">
              {reason}
            </span>
          ) : null}
          <span aria-hidden className="mx-0.5 h-4 w-px bg-pb-border" />
        </>
      ) : null}
      <Tooltip content="Zoom out">
        <Button
          size="sm"
          variant="ghost"
          onClick={onZoomOut}
          aria-label="Zoom out"
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
      </Tooltip>
      {/* DS keep (PR 3): the zoom readout is a live output, not a control. */}
      <output
        aria-live="polite"
        aria-label={`Current zoom ${Math.round(zoom * 100)}%`}
        className="inline-flex h-8 min-w-12 items-center justify-center px-1.5 text-xs font-medium tabular-nums text-pb-text-secondary"
      >
        {Math.round(zoom * 100)}%
      </output>
      <Tooltip content={fitLabel}>
        <Button size="sm" variant="ghost" onClick={onFit} aria-label={fitLabel}>
          <Maximize2 className="h-3.5 w-3.5" />
        </Button>
      </Tooltip>
      <Tooltip content="Zoom in">
        <Button
          size="sm"
          variant="ghost"
          onClick={onZoomIn}
          aria-label="Zoom in"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </Tooltip>
      {miniMap ? (
        /* A toggle: one name, and `pressed` says whether it is on (the teal
           selection, ringed in high contrast). "Hide mini-map, pressed" would
           contradict itself. */
        <Tooltip content="Mini-map">
          <Button
            size="sm"
            variant="ghost"
            onClick={miniMap.onToggle}
            pressed={miniMap.visible}
            aria-label="Mini-map"
          >
            <MapIcon className="h-3.5 w-3.5" />
          </Button>
        </Tooltip>
      ) : null}
    </>
  );
  return (
    <WorkspaceControlGroup
      placement={placement}
      label={label}
      {...(testId ? { "data-testid": testId } : {})}
      {...(geometry ? { "data-geometry": geometry } : {})}
      // The rail paint (SN-01): every ghost Button here takes the 32px pill
      // with primary text and the neutral hover.
      railControls
    >
      {navigation ? (
        <ViewportControlsReveal navigation={navigation}>
          {controls}
        </ViewportControlsReveal>
      ) : (
        controls
      )}
    </WorkspaceControlGroup>
  );
}
